import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save, ChevronDown, ChevronUp, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEPS = [
  { num: 1, name: 'Poop Frequency', type: 'question', defaults: { question: 'How often do you poop?', subtitle: 'It says a lot about your thyroid health.', options: ['1–2 times a day', 'A few times a week', 'Once a week', 'Other'] }},
  { num: 2, name: 'Symptoms', type: 'question', defaults: { question: 'Do you experience any of these symptoms?', subtitle: 'Select all that apply.', options: ['Persistent fatigue or low energy', 'Brain fog or difficulty concentrating', 'Weight gain or difficulty losing weight', 'Cold sensitivity', 'Hair thinning or dry skin', 'Other'] }},
  { num: 3, name: 'Meet Hashi-aid', type: 'info', defaults: { question: 'Meet Hashi-aid.', subtitle: 'The thyroid support formula designed to help restore balance.' }},
  { num: 4, name: 'Support Areas', type: 'question', defaults: { question: 'What areas of health would you most like to support?', subtitle: 'Select all that apply.', options: ['Thyroid function', 'Energy & metabolism', 'Hair, skin & nails', 'Mood & focus', 'Immune health', 'Gut health'] }},
  { num: 5, name: 'Whole Body Health', type: 'info', defaults: { question: 'Hashi-aid supports whole-body health.', subtitle: 'Formulated with clinically studied nutrients that support thyroid function, metabolism, and energy production.' }},
  { num: 6, name: 'Current Supplements', type: 'question', defaults: { question: 'Do you currently take a thyroid support supplement?', subtitle: 'This helps us understand your experience.', options: ['Yes, daily', 'Sometimes', 'No, but I want to', 'No, I don\'t think I need one'] }},
  { num: 7, name: 'Absorption Info', type: 'info', defaults: { question: 'Many supplements aren\'t absorbed efficiently — Hashi-aid is designed for optimal absorption.', subtitle: '' }},
  { num: 8, name: 'Comparison', type: 'info', defaults: { question: 'How Hashi-aid compares', subtitle: '' }},
  { num: 9, name: 'Clinical Research', type: 'question', defaults: { question: 'How important is clinical research to you when choosing a supplement?', subtitle: '', options: ['Very important', 'Somewhat important', 'Not very important'] }},
  { num: 10, name: 'Timeline', type: 'info', defaults: { question: 'Hashi-aid starts supporting your body in as little as 1–2 weeks.', subtitle: 'Users often report improved energy and mental clarity within the first few weeks.' }},
  { num: 11, name: 'Email Capture', type: 'capture', defaults: { question: 'Unlock 50% off and 1 free gift with your email.', subtitle: '' }},
];

const SplitTestCreate = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;
  const preSelectStep = searchParams.get('step');

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [lpLink, setLpLink] = useState('');
  const [ratioControl, setRatioControl] = useState(50);
  const [selectedSteps, setSelectedSteps] = useState({});
  const [expandedStep, setExpandedStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);

  // Auto-select step from URL param (e.g., /admin/split-tests/new?step=2)
  useEffect(() => {
    if (preSelectStep && !isEditing) {
      const stepNum = parseInt(preSelectStep);
      const step = STEPS.find(s => s.num === stepNum);
      if (step) {
        const key = String(stepNum);
        setSelectedSteps({ [key]: { question: '', subtitle: '', options: step.defaults.options ? [...step.defaults.options] : [] } });
        setExpandedStep(stepNum);
        setName(`Step ${stepNum} Test - ${step.name}`);
      }
    }
  }, [preSelectStep, isEditing]);

  useEffect(() => {
    if (isEditing) {
      fetch(`${API_URL}/api/split-tests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(test => {
          setName(test.name || '');
          setSlug(test.slug || '');
          setLpLink(test.lp_link || '');
          setRatioControl(test.ratio?.[0] ?? 50);
          const overrides = {};
          Object.entries(test.step_overrides || {}).forEach(([stepNum, data]) => {
            overrides[stepNum] = data;
          });
          setSelectedSteps(overrides);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    }
  }, [id, isEditing, token]);

  const toggleStep = (stepNum) => {
    const key = String(stepNum);
    if (selectedSteps[key]) {
      const copy = { ...selectedSteps };
      delete copy[key];
      setSelectedSteps(copy);
      if (expandedStep === stepNum) setExpandedStep(null);
    } else {
      const step = STEPS.find(s => s.num === stepNum);
      setSelectedSteps(prev => ({
        ...prev,
        [key]: { question: '', subtitle: '', options: step.defaults.options ? [...step.defaults.options] : [] }
      }));
      setExpandedStep(stepNum);
    }
  };

  const updateOverride = (stepNum, field, value) => {
    setSelectedSteps(prev => ({
      ...prev,
      [String(stepNum)]: { ...prev[String(stepNum)], [field]: value }
    }));
  };

  const updateOption = (stepNum, idx, value) => {
    setSelectedSteps(prev => {
      const step = { ...prev[String(stepNum)] };
      const opts = [...(step.options || [])];
      opts[idx] = value;
      return { ...prev, [String(stepNum)]: { ...step, options: opts } };
    });
  };

  const addOption = (stepNum) => {
    setSelectedSteps(prev => {
      const step = { ...prev[String(stepNum)] };
      return { ...prev, [String(stepNum)]: { ...step, options: [...(step.options || []), ''] } };
    });
  };

  const removeOption = (stepNum, idx) => {
    setSelectedSteps(prev => {
      const step = { ...prev[String(stepNum)] };
      const opts = (step.options || []).filter((_, i) => i !== idx);
      return { ...prev, [String(stepNum)]: { ...step, options: opts } };
    });
  };

  const handleSave = async () => {
    if (!name.trim()) return alert('Please enter a test name');
    setSaving(true);

    const body = {
      name: name.trim(),
      slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, ''),
      lp_link: lpLink.trim(),
      ratio: [ratioControl, 100 - ratioControl],
      step_overrides: selectedSteps
    };

    try {
      const url = isEditing ? `${API_URL}/api/split-tests/${id}` : `${API_URL}/api/split-tests`;
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Failed to save');
        setSaving(false);
        return;
      }
      navigate('/admin/split-tests');
    } catch (err) {
      alert('Failed to save test');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'linear-gradient(135deg, #7874F8, #09C9E9)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      <header className="sticky top-0 z-50 border-b" style={{ background: '#0f0f17', borderColor: '#1e1e2e' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/split-tests" className="text-gray-500 hover:text-gray-300">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-gray-100">{isEditing ? 'Edit Test' : 'Create Split Test'}</h1>
          </div>
          <button
            data-testid="save-test-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #7874F8, #09C9E9)' }}
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Test'}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Basic Info */}
        <div className="rounded-xl p-6 space-y-4" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <h2 className="text-base font-semibold text-gray-200 mb-2">Basic Info</h2>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Test Name *</label>
            <input data-testid="test-name-input" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-lg text-gray-200 text-sm" style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }} placeholder="e.g., Symptom Question V2" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Custom URL Slug</label>
              <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid #2a2a3a' }}>
                <span className="px-3 py-2.5 text-xs text-gray-600" style={{ background: '#0f0f17' }}>{window.location.origin}/</span>
                <input data-testid="test-slug-input" value={slug} onChange={e => setSlug(e.target.value)} className="flex-1 px-3 py-2.5 text-sm text-gray-200" style={{ background: '#1a1a26', border: 'none', outline: 'none' }} placeholder="my-test" />
              </div>
              <p className="text-xs text-gray-600 mt-1">Leave empty to run on the main quiz URL</p>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Landing Page Link</label>
              <input data-testid="test-lp-input" value={lpLink} onChange={e => setLpLink(e.target.value)} className="w-full px-4 py-2.5 rounded-lg text-gray-200 text-sm" style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }} placeholder="https://yoursite.com/offer" />
              <p className="text-xs text-gray-600 mt-1">Where users go after completing the quiz</p>
            </div>
          </div>
        </div>

        {/* Traffic Ratio */}
        <div className="rounded-xl p-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <h2 className="text-base font-semibold text-gray-200 mb-4">Traffic Split</h2>
          <div className="flex items-center gap-6">
            <div className="flex-1">
              <input
                data-testid="ratio-slider"
                type="range" min="10" max="90" value={ratioControl}
                onChange={e => setRatioControl(Number(e.target.value))}
                className="w-full accent-purple-500"
              />
              <div className="flex justify-between mt-2">
                <span className="text-sm text-gray-400">Control: <span className="text-gray-200 font-medium">{ratioControl}%</span></span>
                <span className="text-sm text-gray-400">Variant: <span className="text-cyan-400 font-medium">{100 - ratioControl}%</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Step Overrides */}
        <div className="rounded-xl p-6" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <h2 className="text-base font-semibold text-gray-200 mb-2">Step Overrides</h2>
          <p className="text-sm text-gray-500 mb-4">Select steps to modify for the variant group. Control group always sees original content.</p>
          <div className="space-y-2">
            {STEPS.map((step) => {
              const key = String(step.num);
              const isSelected = !!selectedSteps[key];
              const isExpanded = expandedStep === step.num;
              const override = selectedSteps[key] || {};

              return (
                <div key={step.num} className="rounded-lg overflow-hidden" style={{ border: `1px solid ${isSelected ? '#7874F840' : '#1e1e2e'}` }}>
                  <button
                    data-testid={`step-toggle-${step.num}`}
                    onClick={() => toggleStep(step.num)}
                    className="w-full flex items-center justify-between p-3 text-left"
                    style={{ background: isSelected ? '#7874F810' : '#1a1a26' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-600'}`}>
                        {isSelected && <span className="text-white text-xs font-bold">&#10003;</span>}
                      </div>
                      <span className="text-sm text-gray-300">Step {step.num}: {step.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#2a2a3a', color: '#9ca3af' }}>{step.type}</span>
                    </div>
                    {isSelected && (
                      <span className="text-gray-500" onClick={(e) => { e.stopPropagation(); setExpandedStep(isExpanded ? null : step.num); }}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </span>
                    )}
                  </button>

                  {isSelected && isExpanded && (
                    <div className="p-4 space-y-4" style={{ background: '#0f0f17', borderTop: '1px solid #1e1e2e' }}>
                      {/* Current Content Preview */}
                      <div className="rounded-lg p-3" style={{ background: '#1a1a26', border: '1px solid #252535' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-3.5 h-3.5 text-purple-400" />
                          <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Current Content (Control)</span>
                        </div>
                        <p className="text-sm text-gray-300 mb-1">{step.defaults.question}</p>
                        {step.defaults.subtitle && (
                          <p className="text-xs text-gray-500 mb-2">{step.defaults.subtitle}</p>
                        )}
                        {step.type === 'question' && step.defaults.options && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {step.defaults.options.map((opt, i) => (
                              <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ background: '#252535', color: '#9ca3af' }}>{opt}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Variant Override Fields */}
                      <div className="rounded-lg p-3" style={{ background: 'rgba(9, 201, 233, 0.04)', border: '1px solid rgba(9, 201, 233, 0.15)' }}>
                        <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-3 block">Variant Content (New)</span>
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Question / Heading</label>
                            <input
                              data-testid={`override-question-${step.num}`}
                              value={override.question || ''}
                              onChange={e => updateOverride(step.num, 'question', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg text-sm text-gray-200"
                              style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }}
                              placeholder={step.defaults.question}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Subtitle</label>
                            <input
                              data-testid={`override-subtitle-${step.num}`}
                              value={override.subtitle || ''}
                              onChange={e => updateOverride(step.num, 'subtitle', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg text-sm text-gray-200"
                              style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }}
                              placeholder={step.defaults.subtitle || 'Subtitle (optional)'}
                            />
                          </div>
                          {step.type === 'question' && (
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Options</label>
                              <div className="space-y-2">
                                {(override.options || []).map((opt, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <input
                                      data-testid={`override-option-${step.num}-${idx}`}
                                      value={opt}
                                      onChange={e => updateOption(step.num, idx, e.target.value)}
                                      className="flex-1 px-3 py-1.5 rounded-lg text-sm text-gray-200"
                                      style={{ background: '#1a1a26', border: '1px solid #2a2a3a' }}
                                      placeholder={`Option ${idx + 1}`}
                                    />
                                    <button onClick={() => removeOption(step.num, idx)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                                  </div>
                                ))}
                                <button onClick={() => addOption(step.num)} className="text-xs text-purple-400 hover:text-purple-300">+ Add Option</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SplitTestCreate;
